<template>
  <div
    class="min-h-screen bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8"
  >
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <h2 class="text-center text-2xl md:text-3xl font-extrabold text-white">
        Postavite novu lozinku
      </h2>
      <p class="mt-2 text-center text-sm text-gray-400">
        Unesite vašu novu lozinku
      </p>
    </div>

    <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-gray-800 py-8 px-6 shadow-md sm:rounded-lg sm:px-10 border border-gray-700"
      >
        <!-- Token validation error -->
        <div v-if="tokenError" class="text-center">
          <div class="text-red-400 text-6xl mb-4">✗</div>
          <h3 class="text-xl font-semibold text-white mb-2">
            Neispravan token
          </h3>
          <p class="text-gray-400 mb-6">{{ tokenError }}</p>
          <div class="space-y-3">
            <router-link
              to="/forgot-password"
              class="block w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 text-center"
            >
              Zatražite novi link
            </router-link>
            <router-link
              to="/login"
              class="block text-center text-indigo-400 hover:text-indigo-300"
            >
              Nazad na prijavu
            </router-link>
          </div>
        </div>

        <!-- Reset form -->
        <form v-else @submit.prevent="handleResetPassword" class="space-y-6">
          <FormInput
            id="password"
            name="password"
            type="password"
            label="Nova lozinka"
            v-model="formData.password"
            placeholder="Nova lozinka"
            required
            :error="errors.password"
            @blur="validateField('password')"
          />

          <FormInput
            id="confirm-password"
            name="confirm-password"
            type="password"
            label="Potvrdi lozinku"
            v-model="formData.confirmPassword"
            placeholder="Potvrdi lozinku"
            required
            :error="errors.confirmPassword"
            @blur="validateField('confirmPassword')"
          />

          <!-- Success message -->
          <div
            v-if="successMessage"
            class="bg-green-900 border border-green-700 text-green-300 px-3 py-2 rounded relative text-sm"
            role="alert"
          >
            <span class="block sm:inline">{{ successMessage }}</span>
          </div>

          <!-- Server error message -->
          <div
            v-if="serverError"
            class="bg-red-900 border border-red-700 text-red-300 px-3 py-2 rounded relative text-sm"
            role="alert"
          >
            <span class="block sm:inline">{{ serverError }}</span>
          </div>

          <FormButton
            type="submit"
            :loading="isLoading"
            :disabled="isLoading || !isFormValid"
            class="w-full"
          >
            Postavite novu lozinku
          </FormButton>

          <div v-if="resetSuccess" class="text-center">
            <FormButton @click="goToLogin" class="w-full">
              Idite na prijavu
            </FormButton>
          </div>

          <div v-else class="text-center">
            <router-link
              to="/login"
              class="text-indigo-400 hover:text-indigo-300 text-sm"
            >
              Nazad na prijavu
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FormInput from '@/components/auth/FormInput.vue';
import FormButton from '@/components/auth/FormButton.vue';
import api from '@/services/api';
import formValidator from '@/services/formValidator';

interface ResetFormData {
  password: string;
  confirmPassword: string;
}

interface ResetErrors {
  password: string;
  confirmPassword: string;
}

export default defineComponent({
  name: 'ResetPasswordView',
  components: {
    FormInput,
    FormButton,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const formData = ref<ResetFormData>({
      password: '',
      confirmPassword: '',
    });

    const errors = ref<ResetErrors>({
      password: '',
      confirmPassword: '',
    });

    const isLoading = ref(false);
    const serverError = ref('');
    const successMessage = ref('');
    const tokenError = ref('');
    const resetSuccess = ref(false);
    const resetToken = ref('');

    const isFormValid = computed(() => {
      return (
        formData.value.password &&
        formData.value.confirmPassword &&
        !errors.value.password &&
        !errors.value.confirmPassword
      );
    });

    const validateField = (field: string) => {
      switch (field) {
        case 'password':
          const passwordValidation = formValidator.validatePassword(
            formData.value.password
          );
          errors.value.password = passwordValidation.error;

          // Re-validate confirm password if it's filled
          if (formData.value.confirmPassword) {
            validateField('confirmPassword');
          }
          break;

        case 'confirmPassword':
          const matchValidation = formValidator.validatePasswordMatch(
            formData.value.password,
            formData.value.confirmPassword
          );
          errors.value.confirmPassword = matchValidation.error;
          break;
      }
    };

    const validateForm = (): boolean => {
      validateField('password');
      validateField('confirmPassword');

      return !errors.value.password && !errors.value.confirmPassword;
    };

    const handleResetPassword = async () => {
      if (!validateForm()) {
        return;
      }

      isLoading.value = true;
      serverError.value = '';
      successMessage.value = '';

      try {
        const response = await api.post('/api/auth/reset-password', {
          token: resetToken.value,
          password: formData.value.password,
        });

        if (response.data.status === 'success') {
          successMessage.value = 'Lozinka je uspešno promenjena';
          resetSuccess.value = true;
          // Clear form
          formData.value = { password: '', confirmPassword: '' };
          errors.value = { password: '', confirmPassword: '' };
        }
      } catch (error: any) {
        if (error.response?.data?.message) {
          serverError.value = error.response.data.message;
        } else {
          serverError.value =
            'Došlo je do greške pri postavljanju nove lozinke';
        }
      } finally {
        isLoading.value = false;
      }
    };

    const goToLogin = () => {
      router.push('/login');
    };

    onMounted(() => {
      const token = route.query.token as string;
      if (token && token.length >= 32) {
        resetToken.value = token;
      } else {
        tokenError.value = 'Token za reset lozinke je neispravan ili nedostaje';
      }
    });

    return {
      formData,
      errors,
      isLoading,
      serverError,
      successMessage,
      tokenError,
      resetSuccess,
      isFormValid,
      validateField,
      handleResetPassword,
      goToLogin,
    };
  },
});
</script>
